import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Steps, message, Spin, Alert } from 'antd';
import { WhatsAppOutlined, SyncOutlined, CheckCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

const Synchronize = () => {
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [canComplete, setCanComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomerData();
  }, []);

  useEffect(() => {
    let interval;
    if (syncLoading) {
      interval = setInterval(checkSyncStatus, 10000); // Verificar a cada 10 segundos
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [syncLoading]);

  const fetchCustomerData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const response = await api.get(`/organizer/customer-data/${user.customer_id}`);
      const data = response.data;
      setCustomerData(data);
      
      if (data.groupjid) {
        setCanComplete(true);
        setSyncLoading(false);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      message.error('Erro ao carregar dados do time');
    }
  };

  const checkSyncStatus = async () => {
    await fetchCustomerData();
  };

  const handleSyncClick = async () => {
    setSyncLoading(true);
    setCurrentStep(1);
    
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const response = await fetch('https://automacao.mooblab.com/webhook/sync-group', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'futezap-admin-api-token': ':Wu|_-1;)n0HJF)e@£5Mqm'
        },
        body: JSON.stringify({
          customer_id: user.customer_id,
          token: customerData.uuid
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Resposta não é JSON válido:', jsonError);
        data = { success: false };
      }
      
      if (data.success === true || data.success === 'true') {
        message.success('Sincronização iniciada com sucesso! Aguarde a mensagem no seu grupo...');
      } else {
        {
          const list = Array.isArray(customerData.instance_phones) && customerData.instance_phones.length
            ? customerData.instance_phones
            : [customerData.instance_phone].filter(Boolean);
          const seen = new Set();
          const uniq = [];
          for (const p of list) {
            const d = String(p || '').replace(/\D/g, '');
            if (!d || seen.has(d)) continue;
            seen.add(d);
            uniq.push(p);
          }
          message.error(`Verifique se os números de contatos foram adicionados no grupo. Para funcionar é necessário que sejam adicionados os seguintes contatos: ${uniq.join(' · ')}`, 8);
        }
        setCurrentStep(0);
        setSyncLoading(false);
      }
    } catch (error) {
      console.error('Erro ao iniciar sincronização:', error);
      message.error('Erro ao iniciar sincronização');
      setCurrentStep(0);
      setSyncLoading(false);
    }
  };

  const handleComplete = () => {
    message.success('Sincronização concluída com sucesso!');
    // Forçar atualização do status de sincronização no layout
    window.location.href = '/team-config';
  };

  const steps = [
    {
      title: 'Renomear Grupo',
      description: 'Adicione o código ao nome do seu grupo',
      icon: <WhatsAppOutlined />
    },
    {
      title: 'Sincronizar',
      description: 'Clique em sincronizar e aguarde',
      icon: <SyncOutlined />
    },
    {
      title: 'Concluir',
      description: 'Confirme a sincronização',
      icon: <CheckCircleOutlined />
    }
  ];

  if (!customerData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <WhatsAppOutlined style={{ fontSize: 48, color: '#25D366', marginBottom: 16 }} />
          <Title level={2}>Sincronizar com WhatsApp</Title>
          <Paragraph>
            Para sincronizar seu grupo com o Futezap e receber a lista de confirmação, 
            você precisa seguir as seguintes etapas:
          </Paragraph>
        </div>

        <Steps current={currentStep} style={{ marginBottom: 32 }}>
          {steps.map((step, index) => (
            <Step key={index} title={step.title} description={step.description} icon={step.icon} />
          ))}
        </Steps>

        {!canComplete && (
          <div style={{ marginBottom: 24 }}>
            <Alert
              message="Instruções Importantes"
              description={
                <div>
                  <p><strong>1.</strong> Renomeie o seu grupo adicionando o seguinte código ao nome do grupo:</p>
                  <div style={{ 
                    background: '#f5f5f5', 
                    padding: 12, 
                    borderRadius: 6, 
                    margin: '8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12
                  }}>
                    <span style={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      color: '#1890ff'
                    }}>
                      {customerData.uuid}
                    </span>
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(customerData.uuid);
                        message.success('Código copiado!');
                      }}
                      title="Copiar código"
                    />
                  </div>
                  <p><strong>2.</strong> Adicione os seguintes contatos no grupo:</p>
                  <div style={{ margin: '8px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(() => {
                      // Dedup por digitos — garante que mesmo numero com
                      // formatacoes diferentes nao apareca duplicado.
                      const list = Array.isArray(customerData.instance_phones) && customerData.instance_phones.length
                        ? customerData.instance_phones
                        : [customerData.instance_phone].filter(Boolean);
                      const seen = new Set();
                      const uniq = [];
                      for (const p of list) {
                        const d = String(p || '').replace(/\D/g, '');
                        if (!d || seen.has(d)) continue;
                        seen.add(d);
                        uniq.push(p);
                      }
                      return uniq.map((phone) => (
                        <div key={phone} style={{
                          background: '#f0f8ff', padding: 8, borderRadius: 4,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                          <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{phone}</span>
                          <Button
                            type="text" icon={<CopyOutlined />} size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(phone);
                              message.success('Número copiado!');
                            }}
                            title="Copiar número"
                          />
                        </div>
                      ));
                    })()}
                  </div>
                  <p><strong>3.</strong> Após renomear o grupo e adicionar os contatos, clique no botão "Sincronizar" para o Futezap iniciar a sincronização e enviar uma mensagem no seu grupo.</p>
                  <p><strong>4.</strong> Recebeu a mensagem de sincronização no seu grupo? Então clique no botão "Concluir Sincronização".</p>
                </div>
              }
              type="info"
              showIcon
            />
          </div>
        )}
        
        {canComplete && (
          <div style={{ marginBottom: 24 }}>
            <Alert
              message="Tudo pronto!"
              description="Agora as mensagens serão enviadas no seu grupo conforme os jogadores confirmarem. Clique em concluir para finalizar o processo."
              type="success"
              showIcon
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
          {currentStep === 0 && (
            <Button
              type="primary"
              size="large"
              icon={<SyncOutlined />}
              onClick={handleSyncClick}
              style={{ minWidth: 200 }}
            >
              Sincronizar
            </Button>
          )}

          {currentStep === 1 && (
            <div style={{ textAlign: 'center' }}>
              {syncLoading && !canComplete && (
                <div>
                  <Spin size="large" />
                  <p style={{ marginTop: 16 }}>Aguarde, estamos sincronizando...</p>
                </div>
              )}
              
              {canComplete && (
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleComplete}
                  style={{ minWidth: 200 }}
                >
                  Concluir Sincronização
                </Button>
              )}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Text type="secondary">
            Precisa de ajuda? Entre em contato com o suporte.
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Synchronize;